import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Ban, Check, ChevronLeft, ChevronRight, ExternalLink, Send, Undo2 } from 'lucide-react';
import axiosInstance from '../utils/axiosInstance';
import V2Shell from './V2Shell';

// Scheduled replies: what Replai will send at the daily slot unless blocked.
// Built for speed (Geoff, 2026-10-19): recap, reply, A/B/J/K, next.
const API = 'api/replai/v2/scheduled/';
const JULIA_API = 'api/replai/v2/julia/';
const GORGIAS = 'https://lamptwist.gorgias.com/app/ticket/';
// A Buddy ticket (Lia on Buddy) opens in Buddy, a Gorgias one in Gorgias.
const ticketUrl = (x) => (x.desk === 'buddy' ? x.buddy_url : `${GORGIAS}${x.ticket}`);
const ticketName = (x) => (x.desk === 'buddy' ? `Buddy #${x.buddy_number}` : x.ticket);
const DeskBadge = ({ x, active = false }) => (x.desk === 'buddy' ? (
  <span className={`shrink-0 rounded px-1.5 py-0.5 text-[0.7rem] font-semibold ${active ? 'bg-white/20 text-white' : 'bg-violet-100 text-violet-800'}`}
    title="A Buddy ticket: Lia sends it from Buddy">Buddy · Lia</span>) : null);
const PILL = {
  queued: 'bg-[var(--ask-tint)] text-[var(--ask)]', approved: 'bg-green-100 text-green-800',
  blocked: 'bg-red-100 text-red-800', sent: 'bg-green-100 text-green-800', held: 'bg-amber-100 text-amber-800',
  failed: 'bg-red-100 text-red-800', shadow: 'bg-violet-100 text-violet-800',
  shadow_sent: 'bg-green-100 text-green-800', shadow_held: 'bg-amber-100 text-amber-800',
};
const SHADOW_LABEL = { shadow: 'waiting for its time', shadow_sent: 'would have sent', shadow_held: 'would have held' };
const pct = (v) => (v == null ? '–' : `${v}%`);
const MODE_LABEL = { gorgias: 'Gorgias (Lia)', buddy_shadow: 'Buddy, shadow (Lia writes notes)', buddy_live: 'Buddy, live (Lia sends)' };

// Lia's shadow on Buddy (Aurélien, 2026-10-29): what she would have sent, next to what she did in Gorgias.
const BuddyShadow = () => {
  const [d, setD] = useState(null);
  const [days, setDays] = useState(7);
  useEffect(() => {
    setD(null);
    axiosInstance.get(JULIA_API, { params: { days } }).then(({ data }) => setD(data)).catch(() => setD(false));
  }, [days]);
  if (d === null) return <div className="mt-8 h-72 animate-pulse rounded-lg bg-[var(--line-2)]" />;
  if (d === false) return <p className="mt-8 text-sm text-red-700">Could not load Lia's Buddy shadow.</p>;
  const m = d.metrics;
  const modes = Object.entries(m.modes || {});
  const stats = [
    [m.runs, 'Buddy tickets drafted'], [pct(m.coverage), 'Gorgias twin found'], [pct(m.lia_coverage), 'Gorgias sends covered'],
    [pct(m.agree_decision), 'same decision (queue or not)'], [pct(m.agree_category), 'same category'],
    [pct(m.agree_slot), 'same send time'], [m.similarity_mean ?? '–', 'text similarity with Gorgias (0 to 1)'],
    [pct(m.agree_check), 'same send-time check'], [`${m.would_send}/${m.would_hold}`, 'would send / would hold'],
    [`${m.today}/${m.cap}`, 'drafted today / daily cap'],
  ];
  return (
    <div className="mt-6">
      <div className="flex flex-wrap items-center gap-3 text-sm text-[var(--ink-2)]">
        <span>Mailboxes:</span>
        {modes.length ? modes.map(([a, v]) => <span key={a} className="rounded bg-[var(--line-2)] px-2 py-0.5">{a}: {MODE_LABEL[v] || v}</span>)
          : <span>all on Gorgias (set them in Control)</span>}
        <label className="ml-auto flex items-center gap-2">Last
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="rounded-md border border-[var(--line)] bg-[var(--surface)] px-2 py-1">
            {[1, 7, 14, 30].map((n) => <option key={n} value={n}>{n} day{n > 1 ? 's' : ''}</option>)}
          </select>
        </label>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-5">
        {stats.map(([v, label]) => (
          <div key={label} className="rounded-lg border border-[var(--line)] bg-[var(--surface)] p-3">
            <div className="num text-[1.4rem] font-semibold leading-none">{v}</div>
            <div className="mt-1 text-xs text-[var(--ink-2)]">{label}</div>
          </div>))}
      </div>
      {(m.hold_reasons || []).length > 0 && (
        <p className="mt-3 text-xs text-[var(--ink-3)]">Why she would hold: {m.hold_reasons.map(([r, n]) => `${r} (${n})`).join(' · ')}</p>)}
      <p className="mt-1 text-xs text-[var(--ink-3)]">Errors: {m.errors.runs} runs, {m.errors.notes} notes, {m.errors.compare} comparisons.</p>
      <ul className="mt-4 divide-y divide-[var(--line-2)] rounded-lg border border-[var(--line)] bg-[var(--surface)] text-sm">
        {d.rows.length === 0 && <li className="px-4 py-3 text-[var(--ink-3)]">No shadow reply yet.</li>}
        {d.rows.map((x) => {
          const c = x.compare || {};
          return (
            <li key={x.id} className="flex flex-wrap items-center gap-2 px-4 py-2">
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${PILL[x.status]}`}>{SHADOW_LABEL[x.status] || x.status}</span>
              <a href={x.buddy_url} target="_blank" rel="noreferrer" className="font-medium hover:underline">{x.recap?.customer || ticketName(x)}</a>
              <span className="text-[var(--ink-3)]">{CAT[x.category] || x.category} · {time(x.scheduled_for)}</span>
              <span className="ml-auto text-xs text-[var(--ink-2)]">
                {c.found ? <>Gorgias: <a href={`${GORGIAS}${c.gorgias_ticket}`} target="_blank" rel="noreferrer" className="underline">{c.gorgias_ticket}</a>
                  {' '}{c.lia?.status || 'not queued'}{c.similarity != null ? ` · text ${c.similarity}` : ''}{c.agree_decision === false ? ' · ⚠ other decision' : ''}</>
                  : c.checked_at ? 'Gorgias twin not found' : 'not compared yet'}
              </span>
              {x.hold_reason && <span className="w-full text-xs text-amber-800">{x.hold_reason}</span>}
            </li>);
        })}
      </ul>
    </div>
  );
};
const CAT = { order_status: 'Order status', product_question: 'Product question', pre_sales_inquiry: 'Pre-sales' };
// Replai's confidence at a glance, next to the name (Geoff, 2026-10-23). Green: very sure
// (0.90+ and a 5/5 fact-check), so it goes out at the early time.
const Score = ({ r, active = false }) => {
  const c = r.recap?.confidence;
  if (c == null) return null;
  const sure = r.recap?.very_sure;
  const tone = active ? 'bg-white/20 text-white'
    : sure ? 'bg-green-100 text-green-800' : c >= 85 ? 'bg-[var(--line-2)] text-[var(--ink-2)]' : 'bg-amber-100 text-amber-800';
  return (
    <span className={`num shrink-0 rounded px-1.5 py-0.5 text-[0.7rem] font-semibold ${tone}`}
      title={sure ? 'Very sure: confidence 0.90+ and a 5/5 fact-check, goes out at the early time' : `Replai's confidence: ${c}%`}>
      {c}%{sure ? ' ✓' : ''}
    </span>);
};

const PAGE = 25; // rows per page in both lists (Geoff, 2026-10-20: "a lot of rows")
const Pager = ({ page, pages, setPage }) => (pages > 1 ? (
  <div className="mt-2 flex items-center justify-center gap-2 text-xs text-[var(--ink-3)]">
    <button type="button" disabled={page === 0} onClick={() => setPage(page - 1)} className="rounded p-1 hover:bg-[var(--line-2)] disabled:opacity-30" aria-label="Previous page"><ChevronLeft size={14} /></button>
    <span className="num">page {page + 1} of {pages}</span>
    <button type="button" disabled={page >= pages - 1} onClick={() => setPage(page + 1)} className="rounded p-1 hover:bg-[var(--line-2)] disabled:opacity-30" aria-label="Next page"><ChevronRight size={14} /></button>
  </div>) : null);
const time = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { weekday: 'short', hour: '2-digit', minute: '2-digit' }) : '');

// When a reply leaves, in words: send times differ per reply now.
const sendsAt = (x) => {
  if (x.status === 'approved') return 'Going out now';
  if (x.recap?.approve_first) return 'Goes out after your Approve';
  const at = new Date(x.scheduled_for);
  const mins = Math.round((at - Date.now()) / 60000);
  if (mins <= 0) return 'Going out now';
  const clock = at.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  if (at.toDateString() !== new Date().toDateString()) return `Goes out ${at.toLocaleDateString('en-GB', { weekday: 'long' })} at ${clock}`;
  return `Goes out at ${clock} (in ${mins < 60 ? `${mins} min` : `${Math.floor(mins / 60)} h ${mins % 60} min`})`;
};

// Sent per send day, by how they went out (Geoff, 2026-10-29); blocked and held in the tooltip.
const HOW = [
  { key: 'untouched', label: 'Nobody looked, sent as drafted', color: '#2a78d6' },
  { key: 'approved', label: 'Approved by the team', color: '#1baf7a' },
  { key: 'edited', label: 'Edited by the team first', color: '#4a3aa7' },
];
const dayLabel = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'numeric' });

const SentChart = ({ rows }) => {
  const [hover, setHover] = useState(null);
  const W = 1200, H = 220, padL = 36, padB = 26, padT = 18;
  const totals = rows.map((r) => HOW.reduce((t, o) => t + r[o.key], 0));
  const max = Math.max(5, ...totals);
  const step = Math.pow(10, Math.floor(Math.log10(max)));
  const top = Math.ceil(max / step) * step;
  const band = (W - padL) / Math.max(rows.length, 1);
  const barW = Math.min(28, band * 0.55);
  const y = (v) => padT + (H - padT - padB) * (1 - v / top);
  const sum = totals.reduce((a, b) => a + b, 0);
  return (
    <section className="mt-8 rounded-lg border border-[var(--line)] bg-[var(--surface)] p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="font-semibold">Sent per day <span className="num font-normal text-[var(--ink-3)]">({sum} since {dayLabel(rows[0].date)})</span></h2>
        <div className="flex flex-wrap gap-4 text-xs text-[var(--ink-2)]">
          {HOW.map((o) => <span key={o.key} className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: o.color }} />{o.label}</span>)}
        </div>
      </div>
      <div className="relative mt-3">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="Scheduled replies sent per day, by how they went out">
          {[0, top / 2, top].map((t) => (
            <g key={t}>
              <line x1={padL} x2={W} y1={y(t)} y2={y(t)} stroke="#ebeef0" />
              <text x={padL - 8} y={y(t) + 4} textAnchor="end" fontSize="11" fill="#8a939e">{t}</text>
            </g>
          ))}
          {rows.map((r, i) => {
            const x = padL + i * band + (band - barW) / 2;
            const segs = HOW.filter((o) => r[o.key] > 0);
            let acc = 0;
            return (
              <g key={r.date} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
                <rect x={padL + i * band} y={padT} width={band} height={H - padT - padB} fill="transparent" />
                {segs.map((o, j) => {
                  const y0 = y(acc), y1 = y(acc + r[o.key]);
                  acc += r[o.key];
                  const h = Math.max(0, y0 - y1 - (j < segs.length - 1 ? 2 : 0));
                  return j === segs.length - 1 ? (
                    <path key={o.key} fill={o.color} d={`M${x},${y0} V${Math.min(y0, y1 + 4)} Q${x},${y1} ${x + 4},${y1} H${x + barW - 4} Q${x + barW},${y1} ${x + barW},${Math.min(y0, y1 + 4)} V${y0} Z`} />
                  ) : <rect key={o.key} x={x} y={y0 - h} width={barW} height={h} fill={o.color} />;
                })}
                {totals[i] > 0 && <text x={x + barW / 2} y={y(totals[i]) - 6} textAnchor="middle" fontSize="11" fill="#5a6472">{totals[i]}</text>}
                <text x={x + barW / 2} y={H - 8} textAnchor="middle" fontSize="11" fill="#8a939e">{dayLabel(r.date)}</text>
              </g>
            );
          })}
        </svg>
        {hover != null && (
          <div className="pointer-events-none absolute top-2 rounded-md border border-[var(--line)] bg-[var(--surface)] px-3 py-2 text-sm shadow-sm"
            style={{ left: `${Math.min(75, ((hover + 0.5) / rows.length) * 100)}%` }}>
            <div className="font-semibold">{dayLabel(rows[hover].date)}: {totals[hover]} sent</div>
            {HOW.map((o) => (
              <div key={o.key} className="flex items-center gap-2">
                <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: o.color }} />
                <span className="text-[var(--ink-2)]">{o.label}</span>
                <span className="num ml-auto pl-4">{rows[hover][o.key]}</span>
              </div>
            ))}
            <div className="mt-1 text-[var(--ink-3)]">Blocked: <span className="num">{rows[hover].blocked}</span> · stopped by a send check: <span className="num">{rows[hover].held}</span></div>
          </div>
        )}
      </div>
    </section>
  );
};

const ScheduledV2 = () => {
  const [tab, setTab] = useState('queue');
  const [d, setD] = useState(null);
  const [i, setI] = useState(0);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [donePage, setDonePage] = useState(0);
  const box = useRef(null);
  const load = useCallback(() => axiosInstance.get(API).then(({ data }) => setD(data)).catch(() => setD(false)), []);
  useEffect(() => { load(); }, [load]);
  const queue = d?.queue || [];
  const cur = queue[Math.min(i, Math.max(queue.length - 1, 0))];
  useEffect(() => { setText(cur?.text || ''); setMsg(''); }, [cur?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = useCallback(async (action) => {
    if (!cur || busy) return;
    setBusy(true);
    try {
      const body = { action };
      if (text.trim() && text.trim() !== cur.text.trim()) body.text = text;
      const said = (data) => setMsg(data.result?.sent ? `Sent as ${data.result.sent}.` : `Not sent: ${data.result?.held || data.result?.failed || data.status}`);
      if (action === 'send') {
        if (body.text) await axiosInstance.patch(`${API}${cur.id}/`, { text: body.text });
        const { data } = await axiosInstance.post(`${API}${cur.id}/`);
        said(data);
      } else {
        const { data } = await axiosInstance.patch(`${API}${cur.id}/`, body);
        if (action === 'approve') said(data); // approving sends it right away (Geoff, 2026-10-20)
      }
      await load(); // sent or blocked replies leave the queue, so the next one moves into place
    } catch (e) {
      setMsg(e?.response?.data?.error || 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  }, [cur, text, busy, load]);

  // Undo a block (Geoff, 2026-10-20: blocked by mistake): back in the queue, next send time.
  const undo = useCallback(async (x) => {
    setBusy(true);
    try {
      await axiosInstance.patch(`${API}${x.id}/`, { action: 'queue' });
      await load();
      setMsg(`Back in the queue: ${x.recap?.customer || ticketName(x)}.`);
    } catch (e) {
      setMsg(e?.response?.data?.error || 'Could not put it back in the queue.');
    } finally {
      setBusy(false);
    }
  }, [load]);

  useEffect(() => {
    const onKey = (e) => {
      const typing = e.target === box.current;
      if (typing && (e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); act('approve'); return; }
      if (typing || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'a') act('approve');
      else if (e.key === 'b') act('block');
      else if (e.key === 'j' || e.key === 'ArrowRight') setI((x) => Math.min(x + 1, queue.length - 1));
      else if (e.key === 'k' || e.key === 'ArrowLeft') setI((x) => Math.max(x - 1, 0));
      else if (e.key === 'e') { e.preventDefault(); box.current?.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [act, queue.length]);

  const toReview = queue.filter((x) => x.status === 'queued').length;
  const qi = Math.min(i, Math.max(queue.length - 1, 0));
  const qPage = Math.floor(qi / PAGE);
  const qPages = Math.ceil(queue.length / PAGE);
  const done = d?.done || [];
  const dPages = Math.ceil(done.length / PAGE);
  return (
    <V2Shell>
      <div className="px-6 py-10 sm:px-10 xl:px-14">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-[2.4rem] font-semibold leading-[1.1]">Scheduled replies</h1>
            <p className="prose-body mt-2 max-w-3xl text-[var(--ink-2)]">
              Replies Replai is sure about. Approve one and it goes out right away; the others go out by themselves at the time shown on each,
              usually within 30 minutes of the draft (at most an hour) and spread over the morning for overnight ones, unless you block them. Always from the team account, signed with the team name.
            </p>
          </div>
          {d && <div className="text-right text-sm"><b className="num text-lg">{toReview}</b> to review · <b className="num">{queue.length - toReview}</b> approved</div>}
        </div>
        <div className="mt-6 flex gap-1 border-b border-[var(--line)] text-sm">
          {[['queue', 'Queue'], ['julia', 'Lia (Buddy, ombre)']].map(([k, label]) => (
            <button key={k} type="button" onClick={() => setTab(k)}
              className={`-mb-px border-b-2 px-3 py-2 ${tab === k ? 'border-[var(--ink)] font-medium' : 'border-transparent text-[var(--ink-3)] hover:text-[var(--ink)]'}`}>{label}</button>))}
        </div>
        {tab === 'julia' && <BuddyShadow />}
        {tab === 'queue' && <>
        {d && !d.enabled && <p className="mt-4 rounded-md bg-amber-50 p-3 text-sm text-amber-800">Scheduled sending is off (Control). Nothing in this list will be sent automatically.</p>}
        {d === null && <div className="mt-8 h-72 animate-pulse rounded-lg bg-[var(--line-2)]" />}
        {d === false && <p className="mt-8 text-sm text-red-700">Could not load the queue.</p>}
        {d && !queue.length && <p className="mt-8 rounded-lg border border-dashed border-[var(--line)] p-6 text-sm text-[var(--ink-3)]">Nothing waiting. New ones appear here as Replai drafts them.</p>}
        {cur && (
          <div className="mt-6 grid gap-6 lg:grid-cols-[16rem_minmax(0,1fr)]">
            <div>
            <ul className="max-h-[70vh] space-y-1 overflow-auto">
              {queue.slice(qPage * PAGE, (qPage + 1) * PAGE).map((x, k) => {
                const n = qPage * PAGE + k;
                return (
                <li key={x.id}><button type="button" onClick={() => setI(n)}
                  className={`w-full rounded-md px-3 py-2 text-left text-sm ${x.id === cur.id ? 'bg-[var(--ink)] text-white' : 'hover:bg-[var(--line-2)]'}`}>
                  <div className="flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate font-medium">{x.recap?.customer || ticketName(x)}</span>
                    <DeskBadge x={x} active={x.id === cur.id} />
                    <Score r={x} active={x.id === cur.id} />
                    {x.recap?.approve_first && x.status === 'queued' && <span className={`shrink-0 text-[0.7rem] font-semibold ${x.id === cur.id ? "text-white/80" : "text-amber-700"}`} title="Sent only after an Approve">Approve</span>}
                  </div>
                  <div className={`text-xs ${x.id === cur.id ? 'text-white/70' : 'text-[var(--ink-3)]'}`}>{CAT[x.category] || x.category}{x.twin ? ' · ⚠ same customer' : ''}</div>
                  <div className={`text-xs font-medium ${x.id === cur.id ? 'text-white' : 'text-[var(--ink-2)]'}`}>{sendsAt(x)}</div>
                </button></li>);
              })}
            </ul>
            <Pager page={qPage} pages={qPages} setPage={(p) => setI(p * PAGE)} />
            </div>
            <div>
              <div className="rounded-lg border border-[var(--line)] bg-[var(--surface)] p-5">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${PILL[cur.status]}`}>{cur.status}</span>
                  <span className="font-semibold">{cur.recap?.customer}</span>
                  <Score r={cur} />
                  <DeskBadge x={cur} />
                  <span className="text-[var(--ink-3)]">{CAT[cur.category] || cur.category}</span>
                  <a href={ticketUrl(cur)} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-1 text-[var(--ink-2)] hover:underline">{cur.desk === 'buddy' ? `Buddy #${cur.buddy_number}` : 'Ticket'} <ExternalLink size={12} /></a>
                </div>
                <p className="mb-3 text-sm font-semibold">{sendsAt(cur)}</p>
                {cur.recap?.approve_first && cur.status === 'queued' && (
                  <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
                    Goes out only after an Approve: {cur.recap.approve_first.split(':')[0]}.{cur.recap.approve_first.includes('blocked') ? ' Check that the reply says nothing about it.' : ''}
                  </p>)}
                {cur.twin && (
                  <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
                    Same customer, same order: another reply {cur.twin.status === 'sent' ? 'already went out today' : 'is in this queue'} (ticket{' '}
                    <a href={`${GORGIAS}${cur.twin.ticket}`} target="_blank" rel="noreferrer" className="underline">{cur.twin.ticket}</a>). Send only one: block the other.
                  </p>)}
                <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-[var(--ink-3)]">They wrote</p>
                <p className="mt-1 text-sm text-[var(--ink-2)]">{cur.recap?.message}</p>
                {cur.recap?.why && <><p className="mt-3 text-xs font-semibold uppercase tracking-wide text-[var(--ink-3)]">Why Replai is sure</p>
                  <p className="mt-1 text-sm text-[var(--ink-2)]">{cur.recap.why}</p></>}
                {(cur.recap?.looked_up || []).length > 0 && <p className="mt-2 text-xs text-[var(--ink-3)]">Looked up: {cur.recap.looked_up.join(', ')}</p>}
              </div>
              <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-[var(--ink-3)]">The reply {cur.edited ? '(edited)' : ''}</p>
              <textarea ref={box} value={text} onChange={(e) => setText(e.target.value)} rows={12}
                className="mt-1 w-full rounded-lg border border-[var(--line)] bg-[var(--surface)] p-4 text-[0.95rem] leading-relaxed outline-none focus:border-[var(--ink)]" />
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button type="button" disabled={busy} onClick={() => act('approve')} className="inline-flex items-center gap-1.5 rounded-md bg-[var(--ink)] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"><Check size={15} /> Approve &amp; send <kbd className="ml-1 rounded bg-white/20 px-1 text-xs">A</kbd></button>
                <button type="button" disabled={busy} onClick={() => act('block')} className="inline-flex items-center gap-1.5 rounded-md border border-red-300 px-4 py-2 text-sm font-medium text-red-700 disabled:opacity-50"><Ban size={15} /> Block <kbd className="ml-1 rounded bg-red-50 px-1 text-xs">B</kbd></button>
                <button type="button" disabled={busy} onClick={() => act('send')} className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm text-[var(--ink-2)] hover:bg-[var(--line-2)] disabled:opacity-50"><Send size={15} /> Send now</button>
                <span className="ml-auto flex items-center gap-1 text-sm text-[var(--ink-3)]">
                  <button type="button" onClick={() => setI((x) => Math.max(x - 1, 0))} className="rounded p-1 hover:bg-[var(--line-2)]" aria-label="Previous"><ChevronLeft size={16} /></button>
                  {Math.min(i, queue.length - 1) + 1} / {queue.length}
                  <button type="button" onClick={() => setI((x) => Math.min(x + 1, queue.length - 1))} className="rounded p-1 hover:bg-[var(--line-2)]" aria-label="Next"><ChevronRight size={16} /></button>
                </span>
              </div>
              {msg && <p className="mt-2 text-sm text-[var(--ink-2)]">{msg}</p>}
              <p className="mt-2 text-xs text-[var(--ink-3)]">Keys: A approve and send · B block · J/K next/previous · E edit · ⌘↵ save, approve and send</p>
            </div>
          </div>
        )}
        {d && (d.daily || []).length > 0 && <SentChart rows={d.daily} />}
        {d && done.length > 0 && (
          <section className="mt-10">
            <h2 className="font-semibold">Last two days <span className="num font-normal text-[var(--ink-3)]">({done.length})</span></h2>
            <ul className="mt-2 divide-y divide-[var(--line-2)] rounded-lg border border-[var(--line)] bg-[var(--surface)] text-sm">
              {done.slice(donePage * PAGE, (donePage + 1) * PAGE).map((x) => (
                <li key={x.id} className="flex flex-wrap items-center gap-2 px-4 py-2">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${PILL[x.status]}`}>{x.status}</span>
                  <a href={ticketUrl(x)} target="_blank" rel="noreferrer" className="font-medium hover:underline">{x.recap?.customer || ticketName(x)}</a>
                  <DeskBadge x={x} />
                  <span className="text-[var(--ink-3)]">{x.sent_as ? `as ${x.sent_as}` : x.reviewed_by ? `by ${x.reviewed_by}` : ''}</span>
                  {x.status === 'blocked' && (
                    <button type="button" onClick={() => undo(x)} disabled={busy}
                      className="ml-auto inline-flex items-center gap-1 rounded-md border border-[var(--line)] px-2 py-0.5 text-xs text-[var(--ink-2)] hover:bg-[var(--line-2)] disabled:opacity-50">
                      <Undo2 size={12} /> Undo, back in the queue
                    </button>)}
                  {x.hold_reason && <span className="w-full text-xs text-amber-800">{x.hold_reason}</span>}
                </li>))}
            </ul>
            <Pager page={Math.min(donePage, Math.max(dPages - 1, 0))} pages={dPages} setPage={setDonePage} />
          </section>
        )}
        </>}
      </div>
    </V2Shell>
  );
};

export default ScheduledV2;
